/* see docs/EXTRACTED_COMMENTS.md#c067 */

import type { API } from "@editorjs/editorjs"

export interface TableData {
  withHeadings?: boolean
  content: string[][]
}

const MIN_COLS = 1
const MIN_DATA_ROWS = 1
const PLUS_ICON = `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5 V19 M5 12 H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`
const CARET_ICON = `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 10 L12 15 L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`

/** Compact 16px line icons for the structural menu actions. */
const svg = (body: string): string =>
  `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" width="15" height="15">${body}</svg>`
const ICONS = {
  insertLeft: svg(`<rect x="13" y="4" width="7" height="16" rx="1.5" stroke="currentColor" stroke-width="1.7"/><path d="M6 8 V16 M6 8 V16 M3 12 H9 M6 9 V15" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>`),
  insertRight: svg(`<rect x="4" y="4" width="7" height="16" rx="1.5" stroke="currentColor" stroke-width="1.7"/><path d="M18 9 V15 M15 12 H21" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>`),
  insertAbove: svg(`<rect x="4" y="13" width="16" height="7" rx="1.5" stroke="currentColor" stroke-width="1.7"/><path d="M12 3 V9 M9 6 H15" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>`),
  insertBelow: svg(`<rect x="4" y="4" width="16" height="7" rx="1.5" stroke="currentColor" stroke-width="1.7"/><path d="M12 15 V21 M9 18 H15" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>`),
  duplicate: svg(`<rect x="8" y="8" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.7"/><path d="M4 16 V6 a2 2 0 0 1 2-2 H16" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>`),
  moveLeft: svg(`<path d="M11 6 L5 12 L11 18 M5 12 H20" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`),
  moveRight: svg(`<path d="M13 6 L19 12 L13 18 M19 12 H4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`),
  moveUp: svg(`<path d="M6 11 L12 5 L18 11 M12 5 V20" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`),
  moveDown: svg(`<path d="M6 13 L12 19 L18 13 M12 19 V4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`),
  clear: svg(`<path d="M9 20 H20 M5.5 16.5 L13 9 l4 4 -7.5 7.5 H6.5 z M12 8 l4-4 a1.4 1.4 0 0 1 2 0 l2 2 a1.4 1.4 0 0 1 0 2 l-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`),
  trash: svg(`<path d="M5 7 H19 M10 7 V5 a1 1 0 0 1 1-1 H13 a1 1 0 0 1 1 1 V7 M8 7 l1 12 a1 1 0 0 0 1 1 H14 a1 1 0 0 0 1-1 l1-12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`),
}

/* see docs/EXTRACTED_COMMENTS.md#c368 */
function colLabel(index: number): string {
  let n = index
  let label = ""
  do {
    label = String.fromCharCode(65 + (n % 26)) + label
    n = Math.floor(n / 26) - 1
  } while (n >= 0)
  return label
}

interface MenuItem {
  label: string
  action?: () => void
  danger?: boolean
  disabled?: boolean
  /** Leading icon markup (SVG string). */
  icon?: string
  /** Render a thin divider instead of a button (label ignored). */
  separator?: boolean
}

export class SpreadsheetTableTool {
  static get isReadOnlySupported(): boolean {
    return true
  }

  static get enableLineBreaks(): boolean {
    return true
  }

  static get toolbox() {
    return {
      title: "Table",
      icon: `<svg width="17" height="17" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="2"/>
        <path d="M3 9 H21 M3 14.5 H21 M9 9 V20 M15 9 V20" stroke="currentColor" stroke-width="1.6"/>
      </svg>`,
    }
  }

  static get sanitize() {
    return {
      withHeadings: false,
      content: {
        br: true,
        b: {},
        i: {},
        a: { href: true },
        span: { class: true, style: true },
      },
    }
  }

  private readOnly: boolean
  private data: TableData
  private wrapper: HTMLElement | null = null
  private menu: HTMLElement | null = null
  /* see docs/EXTRACTED_COMMENTS.md#c369 */
  private onChange: () => void = () => {}

  constructor({
    data,
    readOnly,
    config,
  }: {
    data: TableData
    api: API
    readOnly?: boolean
    config?: { onChange?: () => void }
  }) {
    this.readOnly = Boolean(readOnly)
    this.data = { withHeadings: true, content: this.normalize(data?.content) }
    if (typeof config?.onChange === "function") this.onChange = config.onChange
  }

  render(): HTMLElement {
    const wrapper = document.createElement("div")
    wrapper.className = "wdc-grid"
    this.wrapper = wrapper
    // see docs/EXTRACTED_COMMENTS.md#c370
    if (!this.readOnly) {
      wrapper.addEventListener("input", () => this.notifyChange())
    }
    this.build()
    return wrapper
  }

  /* see docs/EXTRACTED_COMMENTS.md#c371 */
  private notifyChange(): void {
    this.onChange()
  }

  save(): TableData {
    this.syncFromDom()
    return { withHeadings: true, content: this.data.content }
  }

  destroy(): void {
    this.closeMenu()
  }

  // --- data helpers ------------------------------------------------------

  private get cols(): number {
    return this.data.content[0]?.length || 0
  }

  private normalize(content: unknown): string[][] {
    let rows: string[][] =
      Array.isArray(content) && content.length
        ? (content as unknown[]).map(row =>
            Array.isArray(row)
              ? row.map(cell => (typeof cell === "string" ? cell : ""))
              : [],
          )
        : [
            ["", "", ""],
            ["", "", ""],
            ["", "", ""],
          ]

    const cols = Math.max(MIN_COLS, ...rows.map(row => row.length))
    rows = rows.map(row => {
      const copy = row.slice()
      while (copy.length < cols) copy.push("")
      return copy
    })
    // see docs/EXTRACTED_COMMENTS.md#c372
    while (rows.length < 1 + MIN_DATA_ROWS) rows.push(new Array(cols).fill(""))
    return rows
  }

  private syncFromDom(): void {
    if (!this.wrapper) return
    const cols = this.cols
    if (!cols) return
    const editables =
      this.wrapper.querySelectorAll<HTMLElement>(".wdc-grid__editable")
    editables.forEach((el, index) => {
      const r = Math.floor(index / cols)
      const c = index % cols
      if (this.data.content[r]) this.data.content[r][c] = el.innerHTML
    })
  }

  // --- rendering ---------------------------------------------------------

  private build(): void {
    const wrapper = this.wrapper
    if (!wrapper) return
    this.closeMenu()
    wrapper.innerHTML = ""

    const cols = this.cols
    const rows = this.data.content.length

    const main = document.createElement("div")
    main.className = "wdc-grid__main"

    const table = document.createElement("div")
    table.className = "wdc-grid__table"
    table.style.setProperty("--cols", String(cols))

    // header row (content[0])
    const corner = document.createElement("div")
    corner.className = "wdc-grid__corner"
    table.appendChild(corner)
    for (let c = 0; c < cols; c++) table.appendChild(this.headCell(c))

    // data rows (content[1..])
    for (let r = 1; r < rows; r++) {
      table.appendChild(this.gutter(r))
      for (let c = 0; c < cols; c++) table.appendChild(this.dataCell(r, c))
    }
    main.appendChild(table)
    wrapper.appendChild(main)

    // see docs/EXTRACTED_COMMENTS.md#c373
    if (!this.readOnly) {
      const addColumn = document.createElement("button")
      addColumn.type = "button"
      addColumn.className = "wdc-grid__addcol"
      addColumn.title = "Add column"
      addColumn.setAttribute("aria-label", "Add column")
      addColumn.innerHTML = PLUS_ICON
      addColumn.addEventListener("click", () => this.insertColumn(this.cols))
      wrapper.appendChild(addColumn)

      const addRow = document.createElement("button")
      addRow.type = "button"
      addRow.className = "wdc-grid__addrow"
      addRow.title = "Add row"
      addRow.setAttribute("aria-label", "Add row")
      addRow.innerHTML = PLUS_ICON
      addRow.addEventListener("click", () => this.insertRow(this.data.content.length))
      wrapper.appendChild(addRow)
    }
  }

  private headCell(c: number): HTMLElement {
    const cell = document.createElement("div")
    cell.className = "wdc-grid__head"

    const text = document.createElement("div")
    text.className = "wdc-grid__editable wdc-grid__headtext"
    text.innerHTML = this.data.content[0]?.[c] ?? ""
    text.dataset.placeholder = colLabel(c)
    if (!this.readOnly) text.contentEditable = "true"
    cell.appendChild(text)

    if (!this.readOnly) {
      const menuButton = document.createElement("button")
      menuButton.type = "button"
      menuButton.className = "wdc-grid__headmenu"
      menuButton.title = "Column options"
      menuButton.innerHTML = CARET_ICON
      const stop = (event: Event) => event.stopPropagation()
      menuButton.addEventListener("pointerdown", stop)
      menuButton.addEventListener("mousedown", stop)
      menuButton.addEventListener("click", event => {
        event.preventDefault()
        event.stopPropagation()
        this.openMenu(menuButton, `col:${c}`, [
          { label: "Insert left", icon: ICONS.insertLeft, action: () => this.insertColumn(c) },
          { label: "Insert right", icon: ICONS.insertRight, action: () => this.insertColumn(c + 1) },
          { label: "Duplicate", icon: ICONS.duplicate, action: () => this.duplicateColumn(c) },
          { separator: true, label: "" },
          { label: "Move left", icon: ICONS.moveLeft, action: () => this.moveColumn(c, c - 1), disabled: c <= 0 },
          { label: "Move right", icon: ICONS.moveRight, action: () => this.moveColumn(c, c + 1), disabled: c >= this.cols - 1 },
          { separator: true, label: "" },
          { label: "Clear column", icon: ICONS.clear, action: () => this.clearColumn(c) },
          {
            label: "Delete column",
            icon: ICONS.trash,
            action: () => this.deleteColumn(c),
            danger: true,
            disabled: this.cols <= MIN_COLS,
          },
        ])
      })
      cell.appendChild(menuButton)
    }
    return cell
  }

  private gutter(r: number): HTMLElement {
    const gutter = document.createElement("button")
    gutter.type = "button"
    gutter.className = "wdc-grid__gutter"
    gutter.disabled = this.readOnly
    gutter.title = `Row ${r} options`

    const number = document.createElement("span")
    number.className = "wdc-grid__gutternum"
    number.textContent = String(r)
    gutter.appendChild(number)

    if (!this.readOnly) {
      const caret = document.createElement("span")
      caret.className = "wdc-grid__guttercaret"
      caret.innerHTML = CARET_ICON
      gutter.appendChild(caret)

      gutter.addEventListener("click", event => {
        event.preventDefault()
        const lastRow = this.data.content.length - 1
        this.openMenu(gutter, `row:${r}`, [
          { label: "Insert above", icon: ICONS.insertAbove, action: () => this.insertRow(r) },
          { label: "Insert below", icon: ICONS.insertBelow, action: () => this.insertRow(r + 1) },
          { label: "Duplicate", icon: ICONS.duplicate, action: () => this.duplicateRow(r) },
          { separator: true, label: "" },
          { label: "Move up", icon: ICONS.moveUp, action: () => this.moveRow(r, r - 1), disabled: r <= 1 },
          { label: "Move down", icon: ICONS.moveDown, action: () => this.moveRow(r, r + 1), disabled: r >= lastRow },
          { separator: true, label: "" },
          { label: "Clear row", icon: ICONS.clear, action: () => this.clearRow(r) },
          {
            label: "Delete row",
            icon: ICONS.trash,
            action: () => this.deleteRow(r),
            danger: true,
            disabled: this.data.content.length - 1 <= MIN_DATA_ROWS,
          },
        ])
      })
    }
    return gutter
  }

  private dataCell(r: number, c: number): HTMLElement {
    const cell = document.createElement("div")
    cell.className = "wdc-grid__editable wdc-grid__cell"
    cell.innerHTML = this.data.content[r]?.[c] ?? ""
    if (!this.readOnly) cell.contentEditable = "true"
    return cell
  }

  // --- structural operations --------------------------------------------

  private insertColumn(at: number): void {
    this.syncFromDom()
    this.data.content = this.data.content.map(row => {
      const copy = row.slice()
      copy.splice(at, 0, "")
      return copy
    })
    this.build()
    this.notifyChange()
  }

  private deleteColumn(at: number): void {
    this.syncFromDom()
    if (this.cols <= MIN_COLS) return
    this.data.content = this.data.content.map(row => {
      const copy = row.slice()
      copy.splice(at, 1)
      return copy
    })
    this.build()
    this.notifyChange()
  }

  private insertRow(at: number): void {
    this.syncFromDom()
    // Never insert above the header row.
    const index = Math.max(1, at)
    this.data.content.splice(index, 0, new Array(this.cols).fill(""))
    this.build()
    this.notifyChange()
  }

  private deleteRow(at: number): void {
    this.syncFromDom()
    // Keep the header plus at least one data row.
    if (at < 1 || this.data.content.length - 1 <= MIN_DATA_ROWS) return
    this.data.content.splice(at, 1)
    this.build()
    this.notifyChange()
  }

  private duplicateColumn(at: number): void {
    this.syncFromDom()
    this.data.content = this.data.content.map(row => {
      const copy = row.slice()
      copy.splice(at + 1, 0, row[at] ?? "")
      return copy
    })
    this.build()
    this.notifyChange()
  }

  private moveColumn(from: number, to: number): void {
    this.syncFromDom()
    if (to < 0 || to >= this.cols || from === to) return
    this.data.content = this.data.content.map(row => {
      const copy = row.slice()
      const [moved] = copy.splice(from, 1)
      copy.splice(to, 0, moved)
      return copy
    })
    this.build()
    this.notifyChange()
  }

  private clearColumn(at: number): void {
    this.syncFromDom()
    // Clear cell contents but keep the header label.
    for (let r = 1; r < this.data.content.length; r++) {
      if (this.data.content[r]) this.data.content[r][at] = ""
    }
    this.build()
    this.notifyChange()
  }

  private duplicateRow(at: number): void {
    this.syncFromDom()
    if (at < 1 || at >= this.data.content.length) return
    this.data.content.splice(at + 1, 0, this.data.content[at].slice())
    this.build()
    this.notifyChange()
  }

  private moveRow(from: number, to: number): void {
    this.syncFromDom()
    // Never move into or past the header row.
    if (from < 1 || to < 1 || to >= this.data.content.length || from === to) return
    const [moved] = this.data.content.splice(from, 1)
    this.data.content.splice(to, 0, moved)
    this.build()
    this.notifyChange()
  }

  private clearRow(at: number): void {
    this.syncFromDom()
    if (at < 1 || at >= this.data.content.length) return
    this.data.content[at] = new Array(this.cols).fill("")
    this.build()
    this.notifyChange()
  }

  // --- header / row menu -------------------------------------------------

  private openMenu(anchor: HTMLElement, key: string, items: MenuItem[]): void {
    const wrapper = this.wrapper
    if (!wrapper) return

    const alreadyOpen = this.menu?.dataset.key === key
    this.closeMenu()
    if (alreadyOpen) return

    const menu = document.createElement("div")
    menu.className = "wdc-grid__menu"
    menu.dataset.key = key

    for (const item of items) {
      if (item.separator) {
        const divider = document.createElement("div")
        divider.className = "wdc-grid__menusep"
        menu.appendChild(divider)
        continue
      }
      const button = document.createElement("button")
      button.type = "button"
      button.className = "wdc-grid__menuitem"
      if (item.danger) button.classList.add("is-danger")
      if (item.disabled) {
        button.classList.add("is-disabled")
        button.disabled = true
      }
      const icon = document.createElement("span")
      icon.className = "wdc-grid__menuicon"
      icon.innerHTML = item.icon ?? ""
      const label = document.createElement("span")
      label.className = "wdc-grid__menulabel"
      label.textContent = item.label
      button.appendChild(icon)
      button.appendChild(label)
      if (!item.disabled) {
        button.addEventListener("click", event => {
          event.preventDefault()
          event.stopPropagation()
          item.action?.()
          this.closeMenu()
        })
      }
      menu.appendChild(button)
    }

    wrapper.appendChild(menu)

    // see docs/EXTRACTED_COMMENTS.md#c374
    const available = wrapper.clientWidth
    if (menu.offsetWidth > available) {
      menu.style.minWidth = "0"
      menu.style.maxWidth = `${available}px`
    }

    // see docs/EXTRACTED_COMMENTS.md#c375
    const wrapperRect = wrapper.getBoundingClientRect()
    const scale =
      wrapper.offsetWidth > 0 ? wrapperRect.width / wrapper.offsetWidth : 1
    const anchorRect = anchor.getBoundingClientRect()
    const anchorX = (anchorRect.left - wrapperRect.left) / scale
    const anchorY = (anchorRect.top - wrapperRect.top) / scale
    const anchorHeight = anchorRect.height / scale

    const menuHeight = menu.offsetHeight
    const menuWidth = menu.offsetWidth

    const maxLeft = Math.max(0, wrapper.clientWidth - menuWidth)
    menu.style.left = `${Math.min(Math.max(0, anchorX), maxLeft)}px`

    // see docs/EXTRACTED_COMMENTS.md#c376
    const spaceBelow = window.innerHeight - anchorRect.bottom
    const spaceAbove = anchorRect.top
    const flipUp =
      spaceBelow < menu.getBoundingClientRect().height && spaceAbove > spaceBelow

    const aboveTop = anchorY - menuHeight - 2
    const belowTop = anchorY + anchorHeight + 2
    menu.style.top = `${Math.max(0, flipUp ? aboveTop : belowTop)}px`

    this.menu = menu
    window.setTimeout(
      () => document.addEventListener("pointerdown", this.onOutside),
      0,
    )
  }

  private onOutside = (event: PointerEvent): void => {
    if (this.menu && !this.menu.contains(event.target as Node)) {
      this.closeMenu()
    }
  }

  private closeMenu(): void {
    if (!this.menu) return
    document.removeEventListener("pointerdown", this.onOutside)
    this.menu.remove()
    this.menu = null
  }
}
